import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../../lib/api'
import styles from './admin.module.css'

export const secs = (ms: number | null | undefined) => ms == null ? '—' : ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : ms < 600_000 ? `${Math.round(ms / 1000)} s` : `${(ms / 60000).toFixed(1)} min`
export const ago = (s: number | null | undefined) => s == null ? '—' : s < 90 ? `${s} s ago` : s < 5400 ? `${Math.round(s / 60)} min ago` : s < 172800 ? `${Math.round(s / 3600)} h ago` : `${Math.round(s / 86400)} days ago`
export const when = (iso: string | null | undefined) => !iso ? '—' : new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(iso))
export const inr = (paise: number) => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`
export const errText = (e: unknown) => e instanceof ApiError ? e.message : 'Something went wrong'

export function Kpi({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return <div className={styles.kpi}><span className={styles.kpiLabel}>{label}</span><span className={styles.kpiValue}>{value}</span>{hint ? <span className={styles.kpiHint}>{hint}</span> : null}</div>
}

export type Tone = 'ok' | 'warn' | 'bad' | 'off'
/** Status pill: colour + dot + word, never colour alone. */
export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`${styles.pill} ${styles[tone]}`}>{children}</span>
}

/** Single-series bar chart with a per-bar hover/focus tooltip (no legend: the card title names the series). */
export function MiniBars({ data, label, format = (n: number) => String(n), xLabel }: {
  data: Array<{ key: string; value: number; label: string }>; label: string; format?: (n: number) => string; xLabel?: [string, string]
}) {
  const max = Math.max(1, ...data.map(d => d.value))
  return (
    <figure className={styles.chart} style={{ margin: 0 }}>
      <div className={styles.bars} role="img" aria-label={`${label}: ${data.map(d => `${d.label} ${format(d.value)}`).join(', ')}`}>
        {data.map(d => (
          <span key={d.key} className={styles.barHit} tabIndex={0} aria-label={`${d.label}: ${format(d.value)}`}>
            <span className={styles.bar} style={{ height: `${(d.value / max) * 100}%` }} />
            <span className={styles.tip}>{d.label} · {format(d.value)}</span>
          </span>
        ))}
      </div>
      {xLabel && <figcaption className={styles.axis}><span>{xLabel[0]}</span><span>max {format(max)}</span><span>{xLabel[1]}</span></figcaption>}
    </figure>
  )
}

/** GET with manual reload and optional polling; keeps the last good value on errors. */
export function useAdmin<T>(path: string, pollMs = 0): { data: T | null; error: string | null; reload: () => Promise<void> } {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    try { setData(await api<T>(path)); setError(null) } catch (e) { setError(errText(e)) }
  }, [path])
  useEffect(() => {
    void reload()
    if (!pollMs) return
    const t = setInterval(() => { if (document.visibilityState === 'visible') void reload() }, pollMs)
    return () => clearInterval(t)
  }, [reload, pollMs])
  return { data, error, reload }
}

export const shortDay = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' })
